import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Card, Table, Tag, Space, Input, DatePicker, Select, Modal as AntModal, Form, Radio, Progress, Empty, Checkbox, Dropdown, Spin, Segmented } from 'antd';
import dayjs from 'dayjs';
import http from '../utils/http';
import { useAuth } from '../context/AuthContext';
import WideModal from '../components/WideModal';
import { getToken } from '../utils/auth';
import { ComposedChart, Bar, Line, CartesianGrid, XAxis, YAxis, Tooltip as ReTooltip, Legend, ResponsiveContainer, RadialBarChart, RadialBar, PolarAngleAxis, PieChart, Pie, Cell } from 'recharts';

// 履职任务页面：参考专项监测结构，适配任务名称和记录逻辑

const ALL_STATUS = 'ALL';
const STATUS_LABEL = { ongoing:'进行中', paused:'暂停', completed:'已完成', stopped:'已终止' };
const STATUS_COLOR = { ongoing:'green', paused:'gold', completed:'blue', stopped:'red' };

const DutyTasksPage = () => {
  const { hasPermission, user } = useAuth() || { hasPermission:()=>false, user:null };
  const canManageTasks = hasPermission(['duty_task_manage']);
  const canManageRecords = hasPermission(['duty_task_record_manage']);
  const canViewAll = hasPermission(['duty_task_view_all']);

  // 任务列表
  const [tasks, setTasks] = useState([]);
  const [rawTasks, setRawTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [statusFilter, setStatusFilter] = useState(ALL_STATUS);
  const [showWithRecordsOnly, setShowWithRecordsOnly] = useState(false);
  const [tasksPage, setTasksPage] = useState(1);
  const [tasksPageSize, setTasksPageSize] = useState(10);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  // 记录列表
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [recordFilters, setRecordFilters] = useState({ task:null, dateRange:null });
  const [recordPage, setRecordPage] = useState(1); const [recordPageSize, setRecordPageSize] = useState(20); const [recordTotal,setRecordTotal]=useState(0);

  // 新建 / 编辑任务
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskSaving, setTaskSaving] = useState(false);
  const [taskForm] = Form.useForm();

  // 风险点排查：用于将风险点填充到“任务名称”下拉
  const [riskPointInspect, setRiskPointInspect] = useState(false);
  const [riskPointOptions, setRiskPointOptions] = useState([]);
  const [riskPointLoading, setRiskPointLoading] = useState(false);

  // 新建记录
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [recordSaving, setRecordSaving] = useState(false);
  const [recordForm] = Form.useForm();

  const authHeader = useCallback(()=> ({ Authorization: 'Bearer '+getToken() }), []);

  useEffect(()=>{
    const onResize = ()=> setWindowWidth(typeof window !== 'undefined' ? window.innerWidth : 1200);
    window.addEventListener('resize', onResize);
    return ()=> window.removeEventListener('resize', onResize);
  }, []);

  // 员工搜索选项（责任人）
  const [empOptions, setEmpOptions] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const debounceRef = useRef();
  const searchEmployees = async (kw) => {
    if(!kw){ setEmpOptions([]); return; }
    setEmpLoading(true);
    if(debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async ()=>{
      try {
        const res = await http.get('/employees', { params:{ name: kw }, headers: authHeader() });
        const list = Array.isArray(res.data)? res.data: [];
        setEmpOptions(list.slice(0,30).map(e=> ({ value:e.id, label:e.name })));
      } catch(e){ /* ignore */ } setEmpLoading(false);
    }, 300);
  };

  const parseResponsibleIds = useCallback((task) => {
    if(!task) return [];
    const raw = task.responsible_ids_array || task.responsible_ids;
    if(Array.isArray(raw)){
      return raw.map(v => Number(v)).filter(v => Number.isFinite(v) && v > 0);
    }
    if(typeof raw === 'string' && raw.trim()){
      const s = raw.trim();
      try {
        const parsed = JSON.parse(s);
        if(Array.isArray(parsed)) return parsed.map(v => Number(v)).filter(v => Number.isFinite(v) && v > 0);
      } catch(e){ /* ignore */ }
      if(s.includes(',')){
        return s.split(',').map(v => Number(String(v).trim())).filter(v => Number.isFinite(v) && v > 0);
      }
    }
    if(task.responsible_id) return [Number(task.responsible_id)].filter(v => Number.isFinite(v) && v > 0);
    return [];
  }, []);

  const loadRiskPoints = useCallback(async () => {
    if (riskPointLoading) return;
    setRiskPointLoading(true);
    try {
      const resp = await http.get('/risk-management/hazard-records', { headers: authHeader() });
      const arr = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp?.data?.data) ? resp.data.data : []);
      const points = (arr || []).filter(r => r && r.source === 'manual_entry');
      const opts = points.map(p => {
        const label = `${p.risk_place_part || ''}${p.risk_place_part ? ' - ' : ''}${p.risk_description || ''}`.trim() || `风险点#${p.id}`;
        return { value: label, label };
      });
      setRiskPointOptions(opts);
    } catch (e) {
      setRiskPointOptions([]);
    } finally {
      setRiskPointLoading(false);
    }
  }, [authHeader, riskPointLoading]);

  // 加载任务
  const loadTasks = useCallback(async ()=>{
    setLoadingTasks(true);
    try {
      const params = {};
      if(statusFilter !== ALL_STATUS) params.status = statusFilter;
      if(showActiveOnly) params.active = '1';
      const res = await http.get('/duty-tasks/tasks', { params, headers: authHeader() });
      let arr = Array.isArray(res.data)? res.data: [];
      // 后端已按权限过滤；这里做一次兼容性过滤（旧后端可能没过滤/多责任人字段不同）
      if(!canViewAll && user?.name){
        arr = arr.filter(t => {
          const namesStr = (t.responsible_names || t.responsible_name || '');
          const names = String(namesStr).split(',').map(s => s.trim()).filter(Boolean);
          return names.includes(user.name);
        });
      }
      setRawTasks(arr);
    } catch(e){ setRawTasks([]); }
    setLoadingTasks(false);
  }, [statusFilter, showActiveOnly, canViewAll, user, authHeader]);
  useEffect(()=>{ loadTasks(); }, [loadTasks]);

  useEffect(()=>{
    let derived = rawTasks.slice();
    if(showActiveOnly){ derived = derived.filter(t=> t.status==='ongoing'); }
    if(showWithRecordsOnly){ derived = derived.filter(t=> (t.record_count||0) > 0); }
    setTasks(derived);
  }, [rawTasks, showActiveOnly, showWithRecordsOnly]);

  // 任务状态切换
  const changeTaskStatus = async (task, newStatus) => {
    if(task.status === newStatus) return;
    const allowed = { ongoing:['paused','completed','stopped'], paused:['ongoing','completed','stopped'] };
    if(!allowed[task.status]?.includes(newStatus)){ return; }
    try {
      await http.put(`/duty-tasks/tasks/${task.id}`, { status:newStatus }, { headers: authHeader() });
      setTasks(prev=> prev.map(t=> t.id===task.id? { ...t, status:newStatus, next_due_date: newStatus==='ongoing'? t.next_due_date: null }: t));
      loadTasks();
    } catch(e){ /* ignore */ }
  };

  const statusCell = (value, record) => {
    const tag = <Tag color={STATUS_COLOR[value]||'default'}>{STATUS_LABEL[value]||value}</Tag>;
    if(!canManageTasks) return tag;
    if(['completed','stopped'].includes(value)) return tag;
    const items = [];
    if(value==='ongoing') items.push({ key:'paused', label:STATUS_LABEL.paused }, { key:'completed', label:STATUS_LABEL.completed }, { key:'stopped', label:STATUS_LABEL.stopped });
    if(value==='paused') items.push({ key:'ongoing', label:STATUS_LABEL.ongoing }, { key:'completed', label:STATUS_LABEL.completed }, { key:'stopped', label:STATUS_LABEL.stopped });
    return <Space size="small">{tag}<Dropdown menu={{ items, onClick:({key})=> changeTaskStatus(record, key) }} trigger={['click']}><span style={{ color:'#1677ff', cursor:'pointer' }}>切换</span></Dropdown></Space>;
  };

  const taskColumns = [
    { title:'任务名称', dataIndex:'name', width:220, ellipsis:true, render:(v)=> v? <span style={{ display:'inline-block', maxWidth:200 }} title={v}>{v}</span>:'—' },
    { title:'责任人', dataIndex:'responsible_name', width:180, render:(_,r)=> {
      const v = r?.responsible_names || r?.responsible_name;
      if(!v) return '—';
      return <span title={v} style={{ display:'inline-block', maxWidth:160, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{v}</span>;
    } },
    { title:'频率', dataIndex:'frequency_type', width:120, render:(_,r)=>{
      switch(r.frequency_type){
        case 'daily': return '每日1次';
        case 'every_n_days': return `每${r.frequency_value}天1次`;
        case 'weekly': return '每周1次';
        case 'monthly': return '每月1次';
        default: return r.frequency_type;
      }
    } },
    { title:'下一次', dataIndex:'next_due_date', width:110, render:(v)=> v||'—' },
  { title:'记录数', dataIndex:'record_count', width:90, render:(v)=> <span style={{ fontWeight: v>0?600:400, color: v>0?'#1677ff':'#666' }}>{v||0}</span> },
    { title:'进度(%)', dataIndex:'progress_percent', width:140, render:(v,r)=>{
      const p = Number(v)||0; return p===0? <span style={{ color:'#999' }}>—</span>: <div style={{ minWidth:120 }}><Progress percent={Math.min(100,p)} size="small" showInfo={false} strokeColor={p>=90?'#52c41a': (p>=70?'#faad14':'#ff4d4f')} /><div style={{ fontSize:11, textAlign:'right' }}>{p.toFixed(2)}%</div></div>; }
    },
    { title:'状态', dataIndex:'status', width:110, render:statusCell },
    { title:'备注', dataIndex:'remark', ellipsis:true },
    { title:'操作', key:'action', width:180, render:(_,r)=> <Space size="small">
      {canManageTasks && <span style={{ color:'#1677ff', cursor:'pointer' }} onClick={()=>{
        setRiskPointInspect(false);
        setEditingTask(r);
        // 兼容：多责任人字段 responsible_ids_array / responsible_ids / responsible_id
        const ids = parseResponsibleIds(r);
        // 确保已选项有 label（不然 Select 可能显示为纯ID）
        if(Array.isArray(r?.responsible_people) && r.responsible_people.length){
          setEmpOptions(prev => {
            const map = new Map((prev || []).map(o => [o.value, o.label]));
            r.responsible_people.forEach(p => { if(p && p.id && p.name) map.set(p.id, p.name); });
            return Array.from(map.entries()).map(([value,label]) => ({ value, label }));
          });
        }
        taskForm.setFieldsValue({ ...r, responsible_ids: ids, start_date: dayjs(r.start_date), end_date: r.end_date? dayjs(r.end_date): null });
        setTaskModalOpen(true);
      }}>编辑</span>}
      {canManageTasks && <span style={{ color:'#ef4444', cursor:'pointer' }} onClick={()=> AntModal.confirm({ title:'删除确认', content:`确定删除任务: ${r.name}?`, onOk: async ()=>{ try { await http.delete(`/duty-tasks/tasks/${r.id}`, { headers: authHeader() }); loadTasks(); } catch(e){ /* ignore */ } } })}>删除</span>}
      {canManageRecords && <span style={{ color:'#1677ff', cursor:'pointer' }} onClick={()=> { setEditingRecord(null); recordForm.resetFields(); recordForm.setFieldsValue({ task_id:r.id, record_date: dayjs(), responsible_id: r.responsible_id }); setRecordModalOpen(true); }}>新增记录</span>}
    </Space> }
  ];

  // 看板汇总
  const [summary, setSummary] = useState({ total:0, ongoing:0, dueToday:0, newRecordsToday:0, avgProgress:null });
  const loadSummary = useCallback(async ()=>{
    try { const res = await http.get('/duty-tasks/dashboard/summary', { headers: authHeader() }); setSummary(res.data||{}); } catch(e){ setSummary({ total:0, ongoing:0, dueToday:0, newRecordsToday:0, avgProgress:null }); }
  }, [authHeader]);
  useEffect(()=>{ loadSummary(); }, [loadSummary, tasks]);

  // 责任人汇总折线/柱状
  const [respSummary, setRespSummary] = useState([]);
  const loadRespSummary = useCallback(async ()=>{
    try { const res = await http.get('/duty-tasks/dashboard/responsible-summary', { headers: authHeader() }); setRespSummary(Array.isArray(res.data)? res.data: []); } catch(e){ setRespSummary([]); }
  }, [authHeader]);
  useEffect(()=>{ loadRespSummary(); }, [loadRespSummary, tasks]);

  // HR 角色识别（roles 可能是数组或逗号字符串）
  const userRoles = Array.isArray(user?.roles)? user.roles : (typeof user?.roles === 'string'? user.roles.split(','): []);
  const isHR = userRoles.some(r=> ['HR','HumanResource','HumanResources','Hr','hr','人力','人事'].includes(String(r).trim()));

  // 计算单任务截至今天的应执行次数（与后端 expectedTotal 逻辑保持一致）
  const todayRef = useRef(dayjs());
  const expectedTotal = useCallback((task)=>{
    if(!task || !task.start_date) return 0;
    const start = dayjs(task.start_date); if(!start.isValid()) return 0;
    // 如果设置了结束日期且已经过去，使用结束日期；否则使用今天
    let end = task.end_date? dayjs(task.end_date): todayRef.current;
    if(end.isBefore(start,'day')) return 0;
    switch(task.frequency_type){
      case 'daily': return end.diff(start,'day')+1;
      case 'every_n_days': { const n=Number(task.frequency_value)||0; if(n<2) return 0; const diff=end.diff(start,'day'); return Math.floor(diff/n)+1; }
      case 'weekly': return end.diff(start,'week')+1;
      case 'monthly': {
        let count=0; let cursor=start.clone(); const dom=start.date();
        while(!cursor.isAfter(end,'day')){ if(cursor.date()===dom) count++; cursor=cursor.add(1,'month').date(dom); }
        return count;
      }
      default: return 0;
    }
  }, []);

  // 估算完成日期：简单按已过天数的平均进度速度推算
  const estimateCompletionDate = useCallback((progressPercent, startDate)=>{
    try {
      if(!startDate) return null;
      const progress = Number(progressPercent)||0;
      if(progress<=0) return null;
      const start = dayjs(startDate);
      const today = todayRef.current;
      const elapsed = Math.max(1, today.diff(start,'day'));
      const avgPerDay = progress / elapsed;
      if(avgPerDay<=0) return null;
      const daysRemaining = Math.ceil((100 - progress)/avgPerDay);
      return today.add(daysRemaining, 'day').format('YYYY-MM-DD');
    } catch(e){ return null; }
  }, []);

  // HR 图表数据（按任务）
  const hrTaskChartData = React.useMemo(()=>{
    if(!isHR) return [];
    return tasks
      .filter(t=> t.status==='ongoing')
      .map(t=> {
        const expected = expectedTotal(t);
        const actual = Number(t.record_count)||0;
        const rate = expected>0? Number(((actual/expected)*100).toFixed(2)) : null;
        return { name: t.name, expected, actual, rate, start_date: t.start_date, progress_percent: Number(t.progress_percent)||0 };
      });
  }, [isHR, tasks, expectedTotal]);

  // 排行图表数据与类型切换
  const rankingData = React.useMemo(()=> {
    const arr = tasks.filter(t=> t.status==='ongoing')
      .sort((a,b)=> (Number(b.progress_percent)||0) - (Number(a.progress_percent)||0));
    return arr.map(t=>{
      const p = Number(t.progress_percent)||0;
      const color = p<50? '#ef4444' : (p<80? '#f59e0b' : '#16a34a');
      return { name: t.name.length>14? t.name.slice(0,14)+'…': t.name, progress: p, color, start_date: t.start_date };
    }).slice(0,10);
  }, [tasks]);
  const [rankingChartType, setRankingChartType] = useState('bar');

  // 记录列表加载
  const loadRecords = useCallback(async ()=>{
    setRecordsLoading(true);
    try {
      const params = { page: recordPage, pageSize: recordPageSize };
      if(recordFilters.task) params.task_id = recordFilters.task;
      if(recordFilters.dateRange){ params.start_date = recordFilters.dateRange[0].format('YYYY-MM-DD'); params.end_date = recordFilters.dateRange[1].format('YYYY-MM-DD'); }
      const res = await http.get('/duty-tasks/records', { params, headers: authHeader() });
      setRecords(Array.isArray(res.data?.records)? res.data.records: []);
      setRecordTotal(res.data?.total||0);
    } catch(e){ setRecords([]); setRecordTotal(0); }
    setRecordsLoading(false);
  }, [recordFilters.task, recordFilters.dateRange, recordPage, recordPageSize, authHeader]);
  useEffect(()=>{ loadRecords(); }, [loadRecords]);

  // 保存任务
  const openNewTask = ()=>{
    setEditingTask(null);
    setRiskPointInspect(false);
    taskForm.resetFields();
    taskForm.setFieldsValue({ frequency_type:'daily', start_date: dayjs() });
    setTaskModalOpen(true);
  };
  const saveTask = async ()=>{
    try {
      const vals = await taskForm.validateFields();
      const responsibleIds = Array.isArray(vals.responsible_ids) ? vals.responsible_ids : (vals.responsible_id ? [vals.responsible_id] : []);
      const normalizedIds = responsibleIds.map(v => Number(v)).filter(v => Number.isFinite(v) && v > 0);
      const payload = {
        name: vals.name,
        description: vals.description,
        // 同时传 responsible_ids + responsible_id(主责任人) 以兼容旧后端
        responsible_ids: normalizedIds,
        responsible_id: normalizedIds[0],
        frequency_type: vals.frequency_type,
        frequency_value: vals.frequency_type==='every_n_days'? vals.frequency_value: undefined,
        start_date: vals.start_date.format('YYYY-MM-DD'),
        end_date: vals.end_date? vals.end_date.format('YYYY-MM-DD'): undefined,
        remark: vals.remark,
        status: vals.status
      };
      setTaskSaving(true);
      if(editingTask){
        await http.put(`/duty-tasks/tasks/${editingTask.id}`, payload, { headers: authHeader() });
      } else {
        await http.post('/duty-tasks/tasks', payload, { headers: authHeader() });
      }
      setTaskModalOpen(false); setTaskSaving(false); loadTasks(); loadSummary(); loadRespSummary();
    } catch(e){ setTaskSaving(false); }
  };

  // 保存记录
  const openNewRecord = ()=>{ setEditingRecord(null); recordForm.resetFields(); recordForm.setFieldsValue({ record_date: dayjs() }); setRecordModalOpen(true); };
  const openEditRecord = (rec) => {
    setEditingRecord(rec);
    recordForm.resetFields();
    recordForm.setFieldsValue({
      task_id: rec.task_id,
      responsible_id: rec.responsible_id,
      record_date: dayjs(rec.record_date),
      record_value: rec.record_value,
      progress_delta: rec.progress_delta,
      status: rec.status,
      remark: rec.remark
    });
    setRecordModalOpen(true);
  };
  const saveRecord = async ()=>{
    try {
      const vals = await recordForm.validateFields();
      const payload = {
        task_id: vals.task_id,
        responsible_id: vals.responsible_id,
        record_date: vals.record_date.format('YYYY-MM-DD'),
        record_value: vals.record_value,
        progress_delta: vals.progress_delta,
        status: vals.status,
        remark: vals.remark
      };
      setRecordSaving(true);
      if(editingRecord){
        await http.put(`/duty-tasks/records/${editingRecord.id}`, payload, { headers: authHeader() });
      } else {
        await http.post('/duty-tasks/records', payload, { headers: authHeader() });
      }
      setRecordSaving(false); setRecordModalOpen(false); loadRecords(); loadTasks(); loadSummary(); loadRespSummary();
    } catch(e){ setRecordSaving(false); }
  };

  const recordColumns = [
    { title:'日期', dataIndex:'record_date', width:110, render:v=> v? dayjs(v).format('YYYY-MM-DD'):'' },
    { title:'任务', dataIndex:'task_name', width:180 },
    { title:'责任人', dataIndex:'responsible_name', width:110 },
    { title:'记录值', dataIndex:'record_value', width:140, ellipsis:true },
    { title:'进度增量', dataIndex:'progress_delta', width:110, render:v=> v!=null? v+'%':'—' },
    { title:'状态', dataIndex:'status', width:90, render:v=> v? <Tag>{v}</Tag>:'—' },
    { title:'备注', dataIndex:'remark', ellipsis:true },
    { title:'操作', key:'act', width:140, render:(_,r)=> canManageRecords? <Space size="small">
      <span style={{ color:'#1677ff', cursor:'pointer' }} onClick={()=> openEditRecord(r)}>编辑</span>
      <span style={{ color:'#ef4444', cursor:'pointer' }} onClick={()=> AntModal.confirm({ title:'删除记录', content:'确定删除该记录?', onOk: async ()=>{ try { await http.delete(`/duty-tasks/records/${r.id}`, { headers: authHeader() }); loadRecords(); loadTasks(); loadSummary(); loadRespSummary(); } catch(e){ /* ignore */ } } })}>删除</span>
    </Space> : null }
  ];

  return (
    <div style={{ padding:24, background:'#f5f7fa', minHeight:'100%' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:600 }}>履职任务工作台</h2>
        <Space>
          {canManageTasks && <Button type="primary" onClick={openNewTask}>新增任务</Button>}
          {canManageRecords && <Button onClick={openNewRecord}>新增记录</Button>}
        </Space>
      </div>

      <Card size="small" style={{ borderRadius:8, marginBottom:16 }} bodyStyle={{ padding:12 }} title={<span>看板汇总</span>} extra={<Space size={4} wrap>
        <Button size="small" onClick={()=>{ loadSummary(); loadRespSummary(); loadTasks(); }}>刷新</Button>
        {!canViewAll && <Tag color="blue">已按当前用户过滤</Tag>}
      </Space>}>
        <div style={{ display:'flex', flexWrap:'wrap', gap:12 }}>
          <div style={{ flex:'1 1 160px', background:'#fafafa', border:'1px solid #eee', borderRadius:8, padding:12 }}>
            <div style={{ fontSize:12, color:'#666' }}>总任务数</div>
            <div style={{ fontSize:22, fontWeight:600 }}>{summary.total}</div>
          </div>
          <div style={{ flex:'1 1 160px', background:'#fafafa', border:'1px solid #eee', borderRadius:8, padding:12 }}>
            <div style={{ fontSize:12, color:'#666' }}>进行中</div>
            <div style={{ fontSize:22, fontWeight:600 }}>{summary.ongoing}</div>
          </div>
          <div style={{ flex:'1 1 160px', background:'#fafafa', border:'1px solid #eee', borderRadius:8, padding:12 }}>
            <div style={{ fontSize:12, color:'#666' }}>今日应执行</div>
            <div style={{ fontSize:22, fontWeight:600 }}>{summary.dueToday}</div>
          </div>
          <div style={{ flex:'1 1 160px', background:'#fafafa', border:'1px solid #eee', borderRadius:8, padding:12 }}>
            <div style={{ fontSize:12, color:'#666' }}>今日新增记录</div>
            <div style={{ fontSize:22, fontWeight:600 }}>{summary.newRecordsToday}</div>
          </div>
          <div style={{ flex:'1 1 160px', background:'#fafafa', border:'1px solid #eee', borderRadius:8, padding:12 }}>
            <div style={{ fontSize:12, color:'#666' }}>平均进度(进行中)</div>
            <div style={{ fontSize:22, fontWeight:600 }}>{summary.avgProgress==null? '—': summary.avgProgress+'%'}</div>
          </div>
        </div>
        {/* 图表区并排 */}
        <div style={{ display:'flex', flexWrap:'wrap', gap:16, marginTop:18, flexDirection: windowWidth < 1200 ? 'column' : 'row' }}>
          <div style={{ flex:'1 1 520px', minWidth:400, height:220, background:'#fff', border:'1px solid #eee', borderRadius:8, padding:8 }}>
          {isHR ? (
            hrTaskChartData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={hrTaskChartData} margin={{ top:10, right:20, left:0, bottom:10 }}>
                  <defs>
                    <linearGradient id="expectedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#94a3b8" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#94a3b8" stopOpacity={0.4} />
                    </linearGradient>
                    <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#16a34a" stopOpacity={0.9} />
                      <stop offset="100%" stopColor="#16a34a" stopOpacity={0.4} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize:10 }} />
                  <YAxis yAxisId="left" label={{ value:'次数', angle:-90, position:'insideLeft', offset:10 }} />
                  <YAxis yAxisId="right" orientation="right" label={{ value:'完成率%', angle:-90, position:'insideRight', offset:10 }} />
                  <ReTooltip content={({ active, payload })=>{
                    if(active && payload && payload.length){ const p = payload[0].payload; const est = estimateCompletionDate(p.progress_percent, p.start_date); return <div style={{ background:'#fff', border:'1px solid #ddd', padding:8, fontSize:12 }}>
                      <div style={{ fontWeight:600 }}>{p.name}</div>
                      <div>期望: {p.expected}</div>
                      <div>实际: {p.actual}</div>
                      <div>完成率: {p.rate==null? '—': p.rate+'%'}</div>
                      <div>预计完成: {est||'—'}</div>
                    </div>; }
                    return null;
                  }} />
                  <Legend wrapperStyle={{ fontSize:11 }} />
                    <Bar yAxisId="left" dataKey="expected" name="期望" stackId="cnt" fill="url(#expectedGradient)" />
                    <Bar yAxisId="left" dataKey="actual" name="实际" stackId="cnt" fill="url(#actualGradient)" />
                  <Line yAxisId="right" type="monotone" dataKey="rate" name="完成率" stroke="#2563eb" strokeWidth={2} dot={{ r:3 }} activeDot={{ r:5 }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize:12 }}>暂无任务图表数据</span>} />
            )
          ) : (
            respSummary.length? <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={respSummary.map(r=> ({ name: r.responsible_name+(r.responsible_id? `(${r.responsible_id})`:''), expected: r.expected, actual: r.actual, rate: r.rate }))} margin={{ top:10, right:20, left:0, bottom:10 }}>
                <defs>
                  <linearGradient id="respExpected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#cbd5e1" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#cbd5e1" stopOpacity={0.4} />
                  </linearGradient>
                  <linearGradient id="respActual" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d399" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#34d399" stopOpacity={0.4} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize:10 }} />
                <YAxis yAxisId="left" label={{ value:'次数', angle:-90, position:'insideLeft', offset:10 }} />
                <YAxis yAxisId="right" orientation="right" label={{ value:'完成率%', angle:-90, position:'insideRight', offset:10 }} />
                  <ReTooltip content={({ active, payload })=>{
                    if(active && payload && payload.length){ const p = payload[0].payload; return <div style={{ background:'#fff', border:'1px solid #ddd', padding:8, fontSize:12 }}>
                      <div style={{ fontWeight:600 }}>{p.name}</div>
                      <div>期望: {p.expected}</div>
                      <div>实际: {p.actual}</div>
                      <div>完成率: {p.rate==null? '—': p.rate+'%'}</div>
                    </div>; }
                    return null;
                  }} />
                <Legend wrapperStyle={{ fontSize:11 }} />
                <Bar yAxisId="left" dataKey="expected" name="期望" stackId="cnt" fill="url(#respExpected)" />
                <Bar yAxisId="left" dataKey="actual" name="实际" stackId="cnt" fill="url(#respActual)" />
                <Line yAxisId="right" type="monotone" dataKey="rate" name="完成率" stroke="#2563eb" strokeWidth={2} dot={{ r:3 }} activeDot={{ r:5 }} />
              </ComposedChart>
            </ResponsiveContainer> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize:12 }}>暂无责任人汇总数据</span>} />
          )}
          </div>
          {/* 小卡：状态占比 */}
          <div style={{ width:160, background:'#fafafa', border:'1px solid #eee', borderRadius:8, padding:8, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
            <div style={{ fontSize:12, color:'#666' }}>任务状态</div>
            <div style={{ width:120, height:80 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={(() => {
                    const map = { ongoing:0, paused:0, completed:0, stopped:0 };
                    rawTasks.forEach(t=> { map[t.status] = (map[t.status]||0) + 1; });
                    return [
                      { name:'进行中', value: map.ongoing||0, color:'#16a34a' },
                      { name:'暂停', value: map.paused||0, color:'#f59e0b' },
                      { name:'已完成', value: map.completed||0, color:'#2563eb' },
                      { name:'已终止', value: map.stopped||0, color:'#ef4444' }
                    ];
                  })()} dataKey="value" nameKey="name" innerRadius={18} outerRadius={36} paddingAngle={2} label={false}>
                    {(() => {
                      const data = (() => { const map = { ongoing:0, paused:0, completed:0, stopped:0 }; rawTasks.forEach(t=> { map[t.status] = (map[t.status]||0) + 1; }); return [
                        { name:'进行中', value: map.ongoing||0, color:'#16a34a' },
                        { name:'暂停', value: map.paused||0, color:'#f59e0b' },
                        { name:'已完成', value: map.completed||0, color:'#2563eb' },
                        { name:'已终止', value: map.stopped||0, color:'#ef4444' }
                      ]; })();
                      return data.map((entry, idx)=>(<Cell key={`pie-${idx}`} fill={entry.color} />));
                    })()}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ marginTop:6, fontSize:12 }}>{`${rawTasks.filter(t=> t.status==='ongoing').length||0} 进行中`}</div>
          </div>
          <div style={{ flex:'1 1 520px', minWidth:400, height:220, background:'#fff', border:'1px solid #eee', borderRadius:8, padding:8, display:'flex', flexDirection:'column' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
              <div style={{ fontSize:13, fontWeight:600 }}>进行中任务进度排行</div>
              <Segmented size="small" options={[{label:'柱状', value:'bar'},{label:'径向', value:'radial'}]} value={rankingChartType} onChange={setRankingChartType} />
            </div>
            <div style={{ flex:1 }}>
              {rankingData.length? (
                rankingChartType==='bar' ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={rankingData} margin={{ top:10, right:10, left:0, bottom:10 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize:10 }} />
                      <YAxis yAxisId="left" label={{ value:'进度%', angle:-90, position:'insideLeft', offset:10 }} domain={[0,100]} />
                      <ReTooltip content={({ active, payload })=>{
                        if(active && payload && payload.length){ const p = payload[0].payload; const est = estimateCompletionDate(p.progress, p.start_date); return <div style={{ background:'#fff', border:'1px solid #ddd', padding:8, fontSize:12 }}>
                          <div style={{ fontWeight:600 }}>{p.name}</div>
                          <div>进度: {p.progress.toFixed(2)}%</div>
                          <div>预计完成: {est||'—'}</div>
                        </div>; }
                        return null;
                      }} />
                      <Legend wrapperStyle={{ fontSize:11 }} />
                      <Bar yAxisId="left" dataKey="progress" name="进度%">
                        {rankingData.map((entry, idx)=>(
                          <Cell key={`cell-${idx}`} fill={entry.color} />
                        ))}
                      </Bar>
                      <Line yAxisId="left" type="monotone" dataKey="progress" name="趋势" stroke="#1d4ed8" strokeWidth={2} dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                      <RadialBarChart data={rankingData} innerRadius="25%" outerRadius="95%" startAngle={90} endAngle={-270}>
                        <PolarAngleAxis type="number" domain={[0,100]} tick={false} />
                        <RadialBar dataKey="progress" name="进度%" cornerRadius={8} background clockWise label={({ value })=> `${value.toFixed(0)}%`}>
                          {rankingData.map((entry, idx) => (
                            <Cell key={`rbar-${idx}`} fill={entry.color} />
                          ))}
                        </RadialBar>
                        <Legend iconType="circle" wrapperStyle={{ fontSize:11 }} />
                        <ReTooltip content={({ active, payload })=>{
                          if(active && payload && payload.length){ const p = payload[0].payload; const est = estimateCompletionDate(p.progress, p.start_date); return <div style={{ background:'#fff', border:'1px solid #ddd', padding:8, fontSize:12 }}>
                            <div style={{ fontWeight:600 }}>{p.name}</div>
                            <div>进度: {p.progress.toFixed(2)}%</div>
                            <div>预计完成: {est||'—'}</div>
                          </div>; }
                          return null;
                        }} />
                      </RadialBarChart>
                  </ResponsiveContainer>
                )
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span style={{ fontSize:12 }}>暂无进行中任务用于排行</span>} />
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card size="small" style={{ borderRadius:8, marginBottom:16 }} bodyStyle={{ padding:12 }} title={<span>任务列表</span>} extra={<Space size={4} wrap>
        <Select size="small" value={statusFilter} style={{ width:110 }} onChange={v=> setStatusFilter(v)} options={[{ value:ALL_STATUS, label:'全部状态' }, ...Object.entries(STATUS_LABEL).map(([k,v])=> ({ value:k, label:v }))]} />
        <Checkbox checked={showActiveOnly} onChange={e=> setShowActiveOnly(e.target.checked)}>仅进行中</Checkbox>
        <Checkbox checked={showWithRecordsOnly} onChange={e=> setShowWithRecordsOnly(e.target.checked)}>仅有记录</Checkbox>
        <Select size="small" value={tasksPageSize} onChange={v=> { setTasksPageSize(Number(v)); setTasksPage(1); }} options={[{ value:10, label:'10/页' }, { value:20, label:'20/页' }, { value:50, label:'50/页' }]} style={{ width:90 }} />
        <Button size="small" onClick={loadTasks}>刷新</Button>
      </Space>}>
        <Table rowKey="id" size="small" scroll={{ x: 1100 }} columns={taskColumns} dataSource={tasks} pagination={{ current: tasksPage, pageSize: tasksPageSize, showSizeChanger: true, pageSizeOptions: ['10','20','50'], onChange: (p, ps)=>{ setTasksPage(p); setTasksPageSize(Number(ps)); } }} loading={loadingTasks} locale={{ emptyText:'暂无任务' }} />
      </Card>

      <Card size="small" style={{ borderRadius:8 }} bodyStyle={{ padding:12 }} title={<span>任务记录</span>} extra={<Space size={4} wrap>
        <Select allowClear placeholder="所属任务" size="small" style={{ width:160 }} value={recordFilters.task} onChange={v=>{ setRecordFilters(f=>({...f, task:v})); setRecordPage(1); }} options={tasks.map(t=> ({ value:t.id, label:t.name }))} />
        <DatePicker.RangePicker size="small" value={recordFilters.dateRange} onChange={v=> { setRecordFilters(f=>({...f, dateRange:v})); setRecordPage(1); }} />
        <Button size="small" onClick={()=>{ setRecordFilters({ task:null, dateRange:null }); setRecordPage(1); }}>重置</Button>
        <Button size="small" onClick={loadRecords}>查询</Button>
        <Select size="small" value={recordPageSize} onChange={v=> { setRecordPageSize(Number(v)); setRecordPage(1); }} options={[{ value:10, label:'10/页' }, { value:20, label:'20/页' }, { value:50, label:'50/页' }, { value:100, label:'100/页' }]} style={{ width:90 }} />
      </Space>}>
        <Table rowKey="id" size="small" columns={recordColumns} dataSource={records} loading={recordsLoading} pagination={{ size:'small', current:recordPage, pageSize:recordPageSize, total:recordTotal, showSizeChanger:true, onChange:(p,ps)=>{ setRecordPage(p); setRecordPageSize(ps); } }} locale={{ emptyText:'暂无记录' }} />
      </Card>

      <WideModal isOpen={taskModalOpen} onClose={()=> setTaskModalOpen(false)} title={(editingTask? '编辑':'新增')+'履职任务'} size="normal">
        <Form form={taskForm} layout="vertical">
          <Form.Item label="" style={{ marginBottom: 8 }}>
            <Checkbox
              checked={riskPointInspect}
              onChange={async (e) => {
                const checked = e.target.checked;
                setRiskPointInspect(checked);
                if (checked) {
                  await loadRiskPoints();
                }
              }}
            >
              风险点排查
            </Checkbox>
          </Form.Item>

          <Form.Item
            name="name"
            label="任务名称 *"
            rules={[{ required:true, message:'请输入任务名称'}]}
          >
            {riskPointInspect ? (
              <Select
                showSearch
                allowClear
                placeholder="请选择风险点（场所/部位 + 风险描述）"
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label || '').toLowerCase().includes((input || '').toLowerCase())}
                loading={riskPointLoading}
                options={riskPointOptions}
                notFoundContent={riskPointLoading ? <Spin size="small" /> : '无匹配风险点'}
              />
            ) : (
              <Input placeholder="例如：月度安全检查" />
            )}
          </Form.Item>
          <Form.Item name="description" label="任务说明">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item
            name="responsible_ids"
            label="责任人 *"
            rules={[
              { required:true, message:'请选择责任人' },
              { validator:(_,v)=> (Array.isArray(v) && v.length>0 ? Promise.resolve() : Promise.reject(new Error('请选择至少1名责任人'))) }
            ]}
          >
            <Select
              mode="multiple"
              showSearch
              placeholder="输入姓名搜索，可多选"
              filterOption={false}
              onSearch={searchEmployees}
              options={empOptions}
              notFoundContent={empLoading? <Spin size="small" />:'无'}
            />
          </Form.Item>
          <Form.Item label="频率类型 *" name="frequency_type" rules={[{ required:true }]}>
            <Radio.Group optionType="button">
              <Radio.Button value="daily">每日</Radio.Button>
              <Radio.Button value="every_n_days">每N天</Radio.Button>
              <Radio.Button value="weekly">每周</Radio.Button>
              <Radio.Button value="monthly">每月</Radio.Button>
            </Radio.Group>
          </Form.Item>
          {taskForm.getFieldValue('frequency_type')==='every_n_days' && <Form.Item name="frequency_value" label="N 天间隔 *" rules={[{ required:true, message:'请输入N(2-365)', pattern:/^([2-9]|[1-9]\d|1\d\d|2\d\d|3[0-5]\d)$/ }]}>
            <Input placeholder="例如 3 表示每3天一次" />
          </Form.Item>}
          <Form.Item name="start_date" label="开始日期 *" rules={[{ required:true }]}>
            <DatePicker style={{ width:'100%' }} />
          </Form.Item>
          <Form.Item name="end_date" label="结束日期">
            <DatePicker style={{ width:'100%' }} />
          </Form.Item>
          <Form.Item name="status" label="状态" initialValue="ongoing">
            <Select options={Object.entries(STATUS_LABEL).map(([k,v])=> ({ value:k, label:v }))} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
        <div style={{ textAlign:'center', marginTop:12 }}>
          <Space>
            <Button type="primary" loading={taskSaving} onClick={saveTask}>保存</Button>
            <Button disabled={taskSaving} onClick={()=> setTaskModalOpen(false)}>取消</Button>
          </Space>
        </div>
      </WideModal>

      <WideModal isOpen={recordModalOpen} onClose={()=> setRecordModalOpen(false)} title={(editingRecord? '编辑':'新增')+'任务记录'} size="normal">
        <Form form={recordForm} layout="vertical">
          <Form.Item name="task_id" label="所属任务 *" rules={[{ required:true, message:'请选择任务'}]}>
            <Select placeholder="选择任务" options={tasks.map(t=>({ value:t.id, label:t.name }))} />
          </Form.Item>
          <Form.Item name="responsible_id" label="责任人 *" rules={[{ required:true, message:'请选择责任人'}]}>
            <Select showSearch placeholder="输入姓名搜索" filterOption={false} onSearch={searchEmployees} options={empOptions} notFoundContent={empLoading? <Spin size="small" />:'无'} />
          </Form.Item>
          <Form.Item name="record_date" label="记录日期 *" rules={[{ required:true }]} initialValue={dayjs()}>
            <DatePicker style={{ width:'100%' }} />
          </Form.Item>
          <Form.Item name="record_value" label="记录值">
            <Input placeholder="如：已完成检查区域A" />
          </Form.Item>
          <Form.Item name="progress_delta" label="进度增量(%)" tooltip="增加的完成百分比, 自动累加到任务进度">
            <Input placeholder="例如 5 表示 +5%" />
          </Form.Item>
          <Form.Item name="status" label="状态标签">
            <Select allowClear options={[{value:'normal', label:'正常'},{value:'blocked', label:'阻塞'},{value:'delayed', label:'延迟'},{value:'risk', label:'风险'}]} />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
        <div style={{ textAlign:'center', marginTop:12 }}>
          <Space>
            <Button type="primary" loading={recordSaving} onClick={saveRecord}>保存</Button>
            <Button disabled={recordSaving} onClick={()=> setRecordModalOpen(false)}>取消</Button>
          </Space>
        </div>
      </WideModal>
    </div>
  );
};

export default DutyTasksPage;
